import { useStreamRFQV2 } from '@hooks/useStreamRFQV2';
import { OtcExchange, OtcOrderType, OtcQuote, OtcQuoteMessage } from '@protos/v2/otcQuote';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type TradingPanel = {
  selectedProduct: string;
  selectedTenor: string | { left: string; right: string };
  tradeSize: number;
  selectedTradingAccount: string;
  selectedExchange: OtcExchange;
  selectedOrderType: OtcOrderType;
  isLockedBuy?: boolean;
  isLockedSell?: boolean;
  lockedBuyPrice?: string;
  lockedSellPrice?: string;
};

type TradingPanelState = {
  prices: { buy: OtcQuote | undefined; sell: OtcQuote | undefined };
  isLoadingPrices: boolean;
  subscriptionSymbol: string;
  subscriptionExchange: OtcExchange;
  setSubscriptionSymbol: (symbol: string) => void;
  setSubscriptionExchange: (exchange: OtcExchange) => void;
  setIsLoadingPrices: (isLoading: boolean) => void;
  setPrices: (prices: { buy: OtcQuote | undefined; sell: OtcQuote | undefined }) => void;
  subscriptionSize: number;
  setSubscriptionSize: (size: number) => void;
  unsubscribeSymbol: () => void;
};

type Props = {
  children: ReactNode;
  isFrozen?: boolean;
};

const TradingContext = createContext<TradingPanelState | undefined>(undefined);
TradingContext.displayName = 'TradingContext';

export default function TradingProvider({ isFrozen, ...otherProps }: Props) {
  const [prices, setPrices] = useState<{ buy: OtcQuote | undefined; sell: OtcQuote | undefined }>({ buy: undefined, sell: undefined });
  const [isLoadingPrices, setIsLoadingPrices] = useState<boolean>(true);
  const [subscriptionSymbol, setSubscriptionSymbol] = useState<string>('');
  const [subscriptionSize, setSubscriptionSize] = useState<number>(1);
  const [subscriptionExchange, setSubscriptionExchange] = useState<OtcExchange>(OtcExchange.ice);

  const [batchedQuoteUpdates, setBatchedQuoteUpdates] = useState<OtcQuoteMessage[]>([]);
  const isFrozenRef = useRef(isFrozen);

  useEffect(() => {
    isFrozenRef.current = isFrozen;
  }, [isFrozen]);

  useEffect(() => {
    if (!isFrozenRef.current && batchedQuoteUpdates.length > 0) {
      const latestUpdate = batchedQuoteUpdates[0];
      const { symbol, product_symbol } = latestUpdate;
      const rfqSymbolKey = typeof symbol === 'string' ? `${product_symbol}${symbol.split(product_symbol)[1]}` : `${symbol.front}-${symbol.back}`;

      if (rfqSymbolKey === subscriptionSymbol) {
        setPrices(prev => ({
          buy: latestUpdate.buy || prev.buy,
          sell: latestUpdate.sell || prev.sell,
        }));
        setIsLoadingPrices(false);
      }

      setBatchedQuoteUpdates([]);
    }
  }, [batchedQuoteUpdates, subscriptionSymbol]);

  const rfqCallback = useCallback(
    (incomingQuoteMessage: OtcQuoteMessage) => {
      const { symbol, product_symbol } = incomingQuoteMessage;
      if (!incomingQuoteMessage.buy && !incomingQuoteMessage.sell) return;
      const rfqSymbolKey = typeof symbol === 'string' ? `${product_symbol}${symbol.split(product_symbol)[1]}` : `${symbol.front}-${symbol.back}`;
      const isValidSymbol = rfqSymbolKey === subscriptionSymbol;

      if (!isValidSymbol) return;

      if (isFrozenRef.current) {
        setBatchedQuoteUpdates(prev => [incomingQuoteMessage, ...prev]);
        return;
      }

      setPrices(prev => ({
        buy: incomingQuoteMessage.buy || prev.buy,
        sell: incomingQuoteMessage.sell || prev.sell,
      }));
      setIsLoadingPrices(false);
    },
    [subscriptionSymbol]
  );

  const { unsubscribeSymbol } = useStreamRFQV2(rfqCallback, subscriptionSymbol, subscriptionSize.toString(), subscriptionExchange);

  const value: TradingPanelState = {
    prices,
    subscriptionSymbol,
    subscriptionSize,
    subscriptionExchange,
    isLoadingPrices,
    setIsLoadingPrices,
    setSubscriptionSymbol,
    setSubscriptionSize,
    setSubscriptionExchange,
    setPrices,
    unsubscribeSymbol,
  };

  return <TradingContext.Provider value={value} {...otherProps} />;
}

export function useTradingContext(): TradingPanelState {
  const context = useContext(TradingContext);

  if (!context) {
    throw new Error('useTradingContext must be used within a TradingProvider');
  }

  return context;
}
